<template>
  <!-- Period pills — same pill design as the shared TabsWrapper tabs -->
  <div class="flex gap-2 w-full items-center overflow-x-auto pb-1">
    <button
      v-for="preset of presets"
      :key="preset.key"
      type="button"
      :class="pillClass(activePreset === preset.key)"
      @click="selectPreset(preset.key)"
    >{{ preset.label }}</button>
    <button
      type="button"
      :class="pillClass(activePreset === 'custom')"
      @click="openRangeSheet"
    >
      <i class="pi pi-calendar text-xs"/>
      {{ customLabel ?? 'טווח מותאם' }}
    </button>
  </div>
</template>

<script setup lang="ts">
import {ref} from 'vue';
import {DateTime} from 'luxon';
import {useDrawer} from '@torim-app/shared-components';

import SettingsDateSheet from '@app/components/admin/pages/configurations/ui/SettingsDateSheet.vue';

type PresetKey = 'today' | 'week' | 'month' | '30d' | '90d' | 'custom';

const emit = defineEmits<{ change: [{ startDate: string; endDate: string; label?: string }] }>();

const {openDrawer} = useDrawer();

const presets: Array<{ key: PresetKey; label: string }> = [
  {key: 'today', label: 'היום'},
  {key: 'week', label: 'השבוע'},
  {key: 'month', label: 'החודש'},
];

const activePreset = ref<PresetKey>('month');
const customLabel = ref<string | null>(null);
const customStart = ref<Date | undefined>(undefined);
const customEnd = ref<Date | undefined>(undefined);

// The exact pill classes of the shared TabsWrapper tabs
const pillClass = (active: boolean) => [
  'w-fit flex-shrink-0 font-normal cursor-pointer px-4 py-1.5 rounded-full border',
  'transition-all duration-300 ease-out flex items-center gap-2 whitespace-nowrap text-sm',
  active
    ? 'bg-primary-500 text-white font-medium border-transparent shadow-sm'
    : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50',
];

const rangeFor = (key: PresetKey): { start: DateTime; end: DateTime } => {
  // Business timezone, not the viewing device's
  const now = DateTime.local({zone: 'Asia/Jerusalem'});
  switch (key) {
    case 'today':
      return {start: now.startOf('day'), end: now.endOf('day')};
    case 'week':
      // Israeli week starts on Sunday (luxon startOf('week') is Monday)
      return {start: now.minus({days: now.weekday % 7}).startOf('day'), end: now.endOf('day')};
    case 'month':
      return {start: now.startOf('month'), end: now.endOf('day')};
    case '30d':
      return {start: now.minus({days: 30}).startOf('day'), end: now.endOf('day')};
    case '90d':
      return {start: now.minus({days: 90}).startOf('day'), end: now.endOf('day')};
    default:
      return {start: now.startOf('month'), end: now.endOf('day')};
  }
};

const presetLabel = (key: PresetKey): string =>
  presets.find(p => p.key === key)?.label ?? 'טווח מותאם';

const selectPreset = (key: PresetKey) => {
  activePreset.value = key;
  const {start, end} = rangeFor(key);
  emit('change', {startDate: start.toISODate() ?? '', endDate: end.toISODate() ?? '', label: presetLabel(key)});
};

// Custom range picks happen in the shared settings date sheet (range mode)
const openRangeSheet = () => {
  openDrawer(SettingsDateSheet, {
    mode: 'range',
    startDate: customStart.value,
    endDate: customEnd.value,
    onPickRange: (pickedStart: Date, pickedEnd: Date) => {
      customStart.value = pickedStart;
      customEnd.value = pickedEnd;
      const start = DateTime.fromJSDate(pickedStart).startOf('day');
      const end = DateTime.fromJSDate(pickedEnd).endOf('day');
      activePreset.value = 'custom';
      customLabel.value = `${start.toFormat('d.M')}–${end.toFormat('d.M')}`;
      emit('change', {
        startDate: start.toISODate() ?? '',
        endDate: end.toISODate() ?? '',
        label: customLabel.value,
      });
    },
  }, {title: 'טווח תאריכים מותאם'});
};

// emit default month range on mount
const {start, end} = rangeFor('month');
emit('change', {startDate: start.toISODate() ?? '', endDate: end.toISODate() ?? '', label: presetLabel('month')});
</script>
